import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Download } from 'lucide-react';
import api from '../../services/api';
import { downloadCsv } from '../../utils/csv';

interface DrillDownData {
  title: string;
  columns: string[];
  rows: (string | number)[][];
}

const MONEY_COLS = new Set(['Montant', 'Payé', 'Reste', 'Montant total', 'Part patient', 'Solde', 'Débit', 'Crédit']);

const fmt = (v: string | number, col: string) => {
  if (MONEY_COLS.has(col) && v !== '' && !isNaN(Number(v))) {
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Number(v)) + ' GNF';
  }
  return String(v ?? '');
};

interface Props {
  metric: string;
  /** Paramètres additionnels (date_debut, date_fin, mode…) */
  params?: Record<string, any>;
  /** Titre de repli en attendant la réponse */
  fallbackTitle?: string;
  onClose: () => void;
}

const DrillDownModal: React.FC<Props> = ({ metric, params, fallbackTitle, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'drilldown', metric, params],
    queryFn: () => api.get<DrillDownData>('/dashboard/analytics/drilldown/', { params: { metric, ...(params || {}) } }).then((r) => r.data),
  });

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(`${metric}.csv`, [data.columns, ...data.rows]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{data?.title || fallbackTitle || 'Détail'}</h3>
          <div className="flex items-center gap-2">
            {data && data.rows.length > 0 && (
              <button onClick={exportCsv}
                className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm px-3 py-1.5 rounded-lg">
                <Download className="h-4 w-4" /> CSV
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="overflow-auto p-1">
          {isLoading || !data ? (
            <div className="p-8 text-center text-gray-500">Chargement…</div>
          ) : data.rows.length === 0 ? (
            <div className="p-8 text-center text-gray-400">Aucune donnée.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  {data.columns.map((c) => (
                    <th key={c} className={`px-4 py-2 ${MONEY_COLS.has(c) ? 'text-right' : ''}`}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    {row.map((cell, j) => (
                      <td key={j} className={`px-4 py-2 text-sm text-gray-700 ${MONEY_COLS.has(data.columns[j]) ? 'text-right font-medium tabular-nums' : ''}`}>
                        {fmt(cell, data.columns[j])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-5 py-2 border-t border-gray-100 text-xs text-gray-400">
          {data ? `${data.rows.length} ligne(s)` : ''}
        </div>
      </div>
    </div>
  );
};

export default DrillDownModal;
